/**
 * Centralized helpers for building Supabase Realtime channel names.
 *
 * Realtime topics are policy-checked server-side against `realtime.topic()`:
 *   - `notifications-<auth.uid()>`   (STRICT equality)
 *   - `messages-<conversation_id>`   (where caller is buyer/seller)
 *
 * UI MUST go through these helpers — never construct channel strings from
 * user-provided input or interpolate untrusted values into the topic. The
 * helpers validate inputs as UUIDs to make crafted-suffix attacks impossible
 * at the call site (the server-side RLS is the real enforcement, but failing
 * fast in the client surfaces bugs early).
 */

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function assertUuid(value: string, label: string): string {
  if (typeof value !== "string" || !UUID_RE.test(value)) {
    throw new Error(
      `[realtimeChannels] ${label} must be a UUID; refusing to build channel name`
    );
  }
  return value.toLowerCase();
}

/** Build the per-user notifications channel topic: `notifications-<uid>` */
export function notificationsChannelFor(userId: string): string {
  return `notifications-${assertUuid(userId, "userId")}`;
}

/** Build the per-conversation messages channel topic: `messages-<conversationId>` */
export function messagesChannelFor(conversationId: string): string {
  return `messages-${assertUuid(conversationId, "conversationId")}`;
}
